import axios, { AxiosHeaders, InternalAxiosRequestConfig, AxiosError } from "axios";
import { getAuctionBaseUrl, getBidBaseUrl, getBookBaseUrl } from '..';
import { AuthenticationService } from '../../Authentication/AuthenticationService';

export const AuctionClient = axios.create({
  baseURL: getAuctionBaseUrl(),
  headers: {
    'content-type': 'application/json',
  },
});

export const BidClient = axios.create({
  baseURL: getBidBaseUrl(),
  headers: {
    'content-type': 'application/json',
  },
});

export const BookClient = axios.create({
  baseURL: getBookBaseUrl(),
  headers: {
    'content-type': 'application/json',
  },
});

// Centralized auth interceptor factory
const createAuthInterceptor = (clientName: string) => {
  return async (config: InternalAxiosRequestConfig) => {
    const headers = new AxiosHeaders();

    try {
      // In dev mode, use developer token if available
      if (import.meta.env.DEV && import.meta.env.REACT_APP_DEVELOPER_ACCESS_TOKEN) {
        headers.set("Authorization", "Bearer " + import.meta.env.REACT_APP_DEVELOPER_ACCESS_TOKEN);
      } else {
        // Get a valid, non-expired token from OIDC
        const token = await AuthenticationService.Instance.getValidToken();
        headers.set("Authorization", `Bearer ${token}`);
      }

      // Preserve existing headers
      if (config.headers) {
        for (const key in config.headers) {
          if (config.headers.hasOwnProperty(key)) {
            headers.set(key, config.headers[key]);
          }
        }
      }

      config.headers = headers;
      return config;
    } catch (error) {
      console.error(`[${clientName}] Authentication error:`, error);
      throw error;
    }
  };
};

// Error handler factory for auth errors with 401 retry logic
const createAuthErrorInterceptor = (client: typeof AuctionClient, clientName: string) => {
  return async (error: AxiosError) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
      (originalRequest as any)._retry = true;
      
      try {
        console.log(`[${clientName}] Received 401, attempting token renewal...`);
        await AuthenticationService.Instance.renewToken();
        
        // Get fresh token and retry
        const token = AuthenticationService.Instance.getUserToken();
        if (originalRequest.headers && token) {
          const retryHeaders = AxiosHeaders.from(originalRequest.headers as any);
           retryHeaders.set('Authorization', `Bearer ${token}`);
           originalRequest.headers = retryHeaders;
          return client(originalRequest);
        } else {
          throw new Error("No token available for retry");
        }
      } catch (renewError) {
        console.error(`[${clientName}] Token renewal failed:`, renewError);
        // Redirect to login
        AuthenticationService.Instance.login(window.location.href);
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  };
};

// Apply request interceptors
AuctionClient.interceptors.request.use(
  createAuthInterceptor("AuctionClient"),
  (error) => Promise.reject(error)
);

BidClient.interceptors.request.use(
  createAuthInterceptor("BidClient"),
  (error) => Promise.reject(error)
);

BookClient.interceptors.request.use(
  createAuthInterceptor("BookClient"),
  (error) => Promise.reject(error)
);

// Apply response interceptors for 401 handling
AuctionClient.interceptors.response.use(
  response => response,
  createAuthErrorInterceptor(AuctionClient, "AuctionClient")
);

BidClient.interceptors.response.use(
  response => response,
  createAuthErrorInterceptor(BidClient, "BidClient")
);

BookClient.interceptors.response.use(
  response => response,
  createAuthErrorInterceptor(BookClient, "BookClient")
);

/**
 * Gets a valid access token from the OIDC authentication service.
 * This function is exported for use by SignalR and other components that need direct token access.
 * @returns Promise<string> - A valid access token
 */
export const getStratosAccessToken = async (): Promise<string> => {
  // In dev mode, use developer token if available
  if (import.meta.env.DEV && import.meta.env.REACT_APP_DEVELOPER_ACCESS_TOKEN) {
    return import.meta.env.REACT_APP_DEVELOPER_ACCESS_TOKEN;
  }
  return AuthenticationService.Instance.getValidToken();
};

